import { useQuery } from "@tanstack/react-query";
import { sortBy } from "es-toolkit";
import AssetActions from "../../actions/AssetActions";
import AssetLibraryActions from "../../actions/AssetLibraryActions";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import AppConstants from "../../constants/AppConstants";
import { ASSET_FOLDERS } from "../../constants/QueryKeys";
import APIError from "../../service/APIError";
import UserService from "../../service/UserService";
import ErrorUtils from "../../utils/ErrorUtils";
import { queryClient, STORE_CACHE_DEFAULTS } from "../../utils/QueryUtils";
import { getUserId } from "./useAccount";

/**
 * @typedef {import("@/types/data/Asset.mjs").AssetFolder} AssetFolder
 * @typedef {{ folders: AssetFolder[] }} AssetFoldersData
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

const defaults = STORE_CACHE_DEFAULTS;

// Register defaults on the shared client so non-hook callers get the same retry/staleTime.
queryClient.setQueryDefaults([ASSET_FOLDERS], defaults);

/**
 * Loads the current user's asset folders (TanStack Query replacement for UserAssetsStore's `folders` list).
 * @param {QueryHookOptions<AssetFoldersData>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ folders: AssetFolder[] | undefined } & QueryResultRest<AssetFoldersData>}
 */
const useAssetFolders = (options = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: [ASSET_FOLDERS],
    queryFn: () => /** @type {Promise<AssetFoldersData>} */ (UserService.getFolders(getUserId())),
    ...defaults,
    ...options,
  });
  return { folders: data?.folders, ...rest };
};

/**
 * One-shot, non-triggering read of the cached folders payload for non-React callers (Reflux stores).
 * @returns {AssetFoldersData | undefined}
 */
export const getAssetFoldersData = () =>
  /** @type {AssetFoldersData | undefined} */ (queryClient.getQueryData([ASSET_FOLDERS]));

/**
 * Writes the ASSET_FOLDERS cache entry for non-React callers.
 * @param {(prev: AssetFoldersData | undefined) => AssetFoldersData} updater
 */
export const setAssetFoldersData = (updater) => queryClient.setQueryData([ASSET_FOLDERS], updater);

/**
 * Pure cache-updater: appends a newly-created folder and re-sorts by name.
 * @param {AssetFoldersData | undefined} prev
 * @param {AssetFolder} folder
 * @returns {AssetFoldersData}
 */
export const insertFolder = (prev, folder) => ({
  ...prev,
  folders: sortBy([...(prev?.folders ?? []), folder], ["name"]),
});

/**
 * Pure cache-updater: renames the folder matching `folder.folder_id` and re-sorts by name.
 * @param {AssetFoldersData | undefined} prev
 * @param {AssetFolder} folder - Must include `folder_id` and the new `name`.
 * @returns {AssetFoldersData}
 */
export const renameFolder = (prev, folder) => ({
  ...prev,
  folders: sortBy(
    (prev?.folders ?? []).map((f) =>
      f.folder_id === folder.folder_id ? { ...f, name: folder.name } : f,
    ),
    ["name"],
  ),
});

/**
 * Pure cache-updater: removes the folder matching `folderId`.
 * @param {AssetFoldersData | undefined} prev
 * @param {string|number} folderId
 * @returns {AssetFoldersData}
 */
export const removeFolder = (prev, folderId) => ({
  ...prev,
  // biome-ignore lint/suspicious/noDoubleEquals: comparing mixed types (string/number) for folder ID matching, mirrors UserAssetsStore.onDeleteFolderComplete's comparison.
  folders: (prev?.folders ?? []).filter((folder) => folder.folder_id != folderId),
});

/**
 * Shared error handler for the folder CRUD actions (create/update/delete/move).
 * @param {Object} err
 */
export function handleFolderActionFailed(err) {
  if (APIError.handle(err)) {
    return;
  }

  const error = ErrorUtils.getFormattedError(err);

  UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
    title: "Folder Action Failed",
    message: error.message,
    alertMode: true,
  });
}

// ---- Module-level action -> cache-write bridges.

UserActions.loadFolders.completed.listen((data) => {
  setAssetFoldersData(() => ({ folders: data.folders }));
});

AssetActions.createFolder.completed.listen((data) => {
  const newFolder = data.folder;

  setAssetFoldersData((prev) => insertFolder(prev, newFolder));

  // Preserves UserAssetsStore.onCreateFolderComplete's side effect of
  // selecting the newly-created folder in the asset library UI.
  AssetLibraryActions.selectFolder(newFolder);
});
AssetActions.createFolder.failed.listen(handleFolderActionFailed);

AssetActions.updateFolder.completed.listen((data) => {
  setAssetFoldersData((prev) => renameFolder(prev, data.folder));
});
AssetActions.updateFolder.failed.listen(handleFolderActionFailed);

AssetActions.deleteFolder.completed.listen((data) => {
  const folderId = data.folder_id;

  if (folderId !== null && folderId !== undefined) {
    setAssetFoldersData((prev) => removeFolder(prev, folderId));
  }
});
AssetActions.deleteFolder.failed.listen(handleFolderActionFailed);

export default useAssetFolders;
